import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CanalRecuperacao, EtapaLogin, Eu, Menu } from "@horofacial/shared";
import { api, ErroRequisicao } from "./api";

export const CHAVE_EU = ["auth", "eu"] as const;

/** Usuário da sessão; null quando não há sessão (401). */
export const consultaEu = queryOptions({
  queryKey: CHAVE_EU,
  queryFn: async (): Promise<Eu | null> => {
    try {
      return await api<Eu>("/auth/eu");
    } catch (e) {
      if (e instanceof ErroRequisicao && e.status === 401) return null;
      throw e;
    }
  },
  staleTime: 5 * 60_000,
  retry: false,
});

export const useEu = () => useQuery(consultaEu);

/** Login e etapas devolvem a próxima etapa; recarrega o "eu" para a interface trocar de tela. */
function useAcaoComEtapa<T>(caminho: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (dados: T) => api<{ etapa: EtapaLogin }>(caminho, { method: "POST", json: dados }),
    onSuccess: () => {
      saiuPorConta = false;
      return cliente.invalidateQueries({ queryKey: CHAVE_EU });
    },
  });
}

/** Saída pedida pelo usuário (Sair): a entrada volta em tela cheia, não por cima da tela anterior. */
let saiuPorConta = false;
export const saidaVoluntaria = () => saiuPorConta;

export const useEntrar = () => useAcaoComEtapa<{ login: string; senha: string }>("/auth/entrar");
/** Esqueci a senha: pede o código pelo canal escolhido e cria a nova senha com ele. */
export const useRecuperarSenha = () =>
  useMutation({ mutationFn: (dados: { login: string; canal: CanalRecuperacao }) => api<{ ok: true }>("/auth/recuperar", { method: "POST", json: dados }) });
export const useConfirmarRecuperacao = () =>
  useMutation({ mutationFn: (dados: { login: string; codigo: string; nova: string; codigo2fa?: string }) => api<{ ok: true }>("/auth/recuperar/confirmar", { method: "POST", json: dados }) });
export const useConfirmar2fa = () => useAcaoComEtapa<{ codigo: string }>("/auth/2fa");
export const useTrocarSenha = () => useAcaoComEtapa<{ atual: string; nova: string }>("/auth/senha");

export function useSair() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () => {
      saiuPorConta = true;
      return fetch("/api/auth/sair", { method: "POST", credentials: "same-origin" });
    },
    onSettled: () => {
      // Marca sem sessão e descarta os dados do usuário anterior (o observador de "eu" continua ligado).
      cliente.setQueryData(CHAVE_EU, null);
      cliente.removeQueries({ predicate: (q) => q.queryKey[0] !== CHAVE_EU[0] || q.queryKey[1] !== CHAVE_EU[1] });
    },
  });
}

/** Menu de nível superior de um caminho: "/pacientes/123/editar" → "/pacientes". */
export function menuDoCaminho(caminho: string): string {
  return caminho === "/" ? "/" : `/${caminho.split("/")[1]}`;
}

/** Caminhos sempre liberados para quem está logado. */
const LIVRES = ["/", "/conta"];

export function podeVer(eu: Eu | null | undefined, caminho: string): boolean {
  if (!eu) return false;
  const menu = menuDoCaminho(caminho);
  if (LIVRES.includes(menu)) return true;
  // Administrador da clínica (e o master agindo como master): permissão completa.
  if (eu.usuario.perfil === "admin") return true;
  return eu.usuario.menus.includes(menu as Menu);
}

/**
 * Tela inicial do perfil: recepção e profissional trabalham na agenda e começam nela (o Início some do menu);
 * os outros perfis começam no Início. Sem a agenda liberada, todos ficam no Início.
 */
export function inicioDoPerfil(eu: Eu): "/" | "/agenda" {
  return (eu.usuario.perfil === "recepcao" || eu.usuario.perfil === "profissional") && podeVer(eu, "/agenda") ? "/agenda" : "/";
}

/** Além de ver, pode lançar e alterar: o menu não está liberado como "só vê". */
export function podeEditar(eu: Eu | null | undefined, caminho: string): boolean {
  if (!eu || !podeVer(eu, caminho)) return false;
  return eu.usuario.perfil === "admin" || !eu.usuario.somenteLeitura.includes(menuDoCaminho(caminho) as Menu);
}
